
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, Printer, CheckSquare } from "lucide-react";
import { AgreementGenerator } from "./AgreementGenerator";
import ChecklistLetter from "./ChecklistLetter";
import { useState, createContext, useContext } from "react";

// Shared state types
interface AgreementData {
  agreementType: "partial" | "full";
  date: Date | undefined;
  primarySellerName: string;
  secondarySellerName: string;
  brokerCompanyName: string;
  propertyAddress: string;
  paperType: string;
  balance: string;
  brokerOfferToSeller: string;
  firstPaymentDate: string;
  dateOfBalance: string;
  partialDollarAmount?: string;
  partialEndDate?: string;
  originalDownPayment?: string;
  recipientEmail?: string;
}

interface ChecklistItem {
  id: string;
  text: string;
}

interface ChecklistLetterData {
  recipientName: string;
  recipientEmail: string;
  recipientAddress: string;
  checklistItems: ChecklistItem[];
}

interface SharedDataContextType {
  agreementData: AgreementData;
  setAgreementData: (data: AgreementData | ((prev: AgreementData) => AgreementData)) => void;
  checklistData: ChecklistLetterData;
  setChecklistData: (data: ChecklistLetterData | ((prev: ChecklistLetterData) => ChecklistLetterData)) => void;
}

// Create context for shared data
const SharedDataContext = createContext<SharedDataContextType | undefined>(undefined);

export const useSharedData = () => {
  const context = useContext(SharedDataContext);
  if (!context) {
    throw new Error('useSharedData must be used within a SharedDataProvider');
  }
  return context;
};

export function AgreementsPage() {
  const [activeTab, setActiveTab] = useState<'agreements' | 'checklist'>('agreements');
  
  // Shared state that persists across tab switches
  const [agreementData, setAgreementData] = useState<AgreementData>({
    agreementType: "partial",
    date: undefined,
    primarySellerName: "",
    secondarySellerName: "",
    brokerCompanyName: "Contract Funding Group, LLC",
    propertyAddress: "",
    paperType: "land contract",
    balance: "",
    brokerOfferToSeller: "",
    firstPaymentDate: "",
    dateOfBalance: "",
    partialDollarAmount: "",
    partialEndDate: "",
    originalDownPayment: "",
    recipientEmail: "",
  });

  const [checklistData, setChecklistData] = useState<ChecklistLetterData>({
    recipientName: '',
    recipientEmail: '',
    recipientAddress: '',
    checklistItems: []
  });

  const sharedDataValue = {
    agreementData,
    setAgreementData,
    checklistData,
    setChecklistData
  };

  return (
    <SharedDataContext.Provider value={sharedDataValue}>
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <FileText className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold">Agreements & Documents</h1>
            <p className="text-muted-foreground">Generate agreements and checklist letters for customers</p>
          </div>
        </div>

        <div className="flex gap-2">
          <Button 
            variant={activeTab === 'agreements' ? 'default' : 'outline'}
            onClick={() => setActiveTab('agreements')}
            className="flex items-center gap-2"
          >
            <FileText className="h-4 w-4" />
            Purchase Agreements
          </Button>
          <Button 
            variant={activeTab === 'checklist' ? 'default' : 'outline'}
            onClick={() => setActiveTab('checklist')}
            className="flex items-center gap-2"
          >
            <CheckSquare className="h-4 w-4" />
            Checklist Letters
          </Button>
        </div>

        {activeTab === 'agreements' && (
          <>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Printer className="h-5 w-5" />
                  Agreement Generator & Printing
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Create both partial and full purchase agreements. Fill in the customer and property details, 
                  then use the print function to generate a professional agreement document.
                </p>
                
                <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg mb-6">
                  <h4 className="font-semibold text-amber-800 mb-2">Agreement Types Available:</h4>
                  <ul className="text-sm text-amber-700 space-y-1">
                    <li>• <strong>Partial Purchase Agreement</strong> - For purchasing specific payment amounts over a defined period</li>
                    <li>• <strong>Full Purchase Agreement</strong> - For purchasing the entire remaining contract balance</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            <AgreementGenerator />
          </>
        )}

        {activeTab === 'checklist' && <ChecklistLetter />}
      </div>
    </SharedDataContext.Provider>
  );
}
